import { Link, useParams } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";
import { getChannel } from "@/mocks/channels";
import { articles, getArticlesByChannel } from "@/mocks/articles";
import { toneClasses } from "@/lib/ui";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import ChannelSidebar from "@/components/feature/ChannelSidebar";
import SectionHeading from "@/components/base/SectionHeading";

const SITE = "https://zozxnbl.cn";

export default function Pindao() {
  const { slug = "" } = useParams();
  const channel = getChannel(slug);
  const list = getArticlesByChannel(slug);

  useSeo({
    title: channel
      ? `${channel.name} | 51爆料网内容板块入口`
      : "内容板块 | 51爆料网",
    description: channel
      ? `${channel.name}频道：${channel.description.slice(0, 60)}。共收录 ${list.length} 篇内容，可直接浏览文章列表。`
      : "51爆料网内容板块入口，按栏目浏览深度调查、实时热点与消费生活类内容。",
    keywords: channel
      ? `${channel.name},51爆料网,${channel.tagline},内容板块入口`
      : "51爆料网,内容板块,栏目导航",
    canonical: `/pindao/${slug}`,
    jsonLd: channel
      ? [
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${channel.name} - 51爆料网`,
            url: `${SITE}/pindao/${channel.slug}`,
            inLanguage: "zh-CN",
            description: channel.description,
            hasPart: list.map((a) => ({
              "@type": "Article",
              headline: a.title,
              url: `${SITE}/wenzhang/${a.slug}`,
            })),
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: SITE },
              { "@type": "ListItem", position: 2, name: "栏目导航", item: `${SITE}/lanmu` },
              {
                "@type": "ListItem",
                position: 3,
                name: channel.name,
                item: `${SITE}/pindao/${channel.slug}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!channel) {
    return (
      <div className="mx-auto max-w-[1280px] px-6 py-24 text-center">
        <i className="ri-error-warning-line text-5xl text-primary-500" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">未找到该内容板块</h1>
        <p className="mt-2 text-sm text-foreground-600">你访问的栏目可能已调整，请从栏目导航页重新选择。</p>
        <Link
          to="/lanmu"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-compass-3-line" />
          前往栏目导航
        </Link>
      </div>
    );
  }

  const tone = toneClasses[channel.tone];
  const others = articles.filter((a) => a.channel !== channel.slug).slice(0, 4);

  return (
    <>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-6 py-8 md:py-10">
          <Breadcrumb items={[{ label: "栏目导航", to: "/lanmu" }, { label: channel.name }]} />
          <div className="mt-5 flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
            <div className="flex items-start gap-4">
              <span className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-lg ${tone.bgSoft} ${tone.textStrong}`}>
                <i className={`${channel.icon} text-3xl`} />
              </span>
              <div>
                <h1 className="font-heading text-3xl font-black text-foreground-950">
                  {channel.name}
                </h1>
                <p className={`mt-1 text-sm font-medium ${tone.textSoft}`}>{channel.tagline}</p>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
                  {channel.description}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap gap-1.5">
              {channel.highlights.map((h) => (
                <span key={h} className="rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-600">
                  {h}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1280px] px-6 py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <SectionHeading
              eyebrow={`${channel.group} · 共 ${list.length} 篇`}
              title={`${channel.name}最新内容`}
            />
            {list.length > 0 ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                {list.map((a) => (
                  <ArticleCard key={a.slug} article={a} variant="default" />
                ))}
              </div>
            ) : (
              <p className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-12 text-center text-sm text-foreground-500">
                此板块内容正在整理中，可先浏览其他栏目。
              </p>
            )}
          </div>

          <aside className="lg:col-span-4">
            <ChannelSidebar />
            <div className="mt-6">
              <h2 className="mb-3 font-heading text-base font-bold text-foreground-950">其他板块推荐</h2>
              <div className="space-y-3">
                {others.map((a) => (
                  <ArticleCard key={a.slug} article={a} variant="row" />
                ))}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </>
  );
}